// src/components/DealsManager.js
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Switch,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../utils/axiosInstance";

const highlightOptions = ["lunch", "snacks", "fruits", "weekend", "special_offer"];

const sortOptions = [
  { label: "Newest", value: { sortBy: "created_at", order: "DESC" } },
  { label: "Oldest", value: { sortBy: "created_at", order: "ASC" } },
  { label: "Highest Discount", value: { sortBy: "discount", order: "DESC" } },
  { label: "Lowest Discount", value: { sortBy: "discount", order: "ASC" } },
  { label: "Starting Soon", value: { sortBy: "start_time", order: "ASC" } },
  { label: "Ending Soon", value: { sortBy: "end_time", order: "ASC" } },
];

const DealsManager = ({ shopSlug, isPremium }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [deals, setDeals] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    discount: "",
    product_id: "",
    start_time: "",
    end_time: "",
    highlight_type: "lunch",
    is_featured: false,
  });
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [sortBy, setSortBy] = useState("created_at");
  const [order, setOrder] = useState("DESC");

  // Fetch deals from backend
  const fetchDeals = async () => {
    if (!shopSlug) return;
    try {
      setLoading(true);
      const res = await api.get("/deals", { params: { shopSlug } });
      let data = res.data || [];

      // Apply sorting client-side (since backend doesn't support sort params yet)
      data.sort((a, b) => {
        if (order === "ASC") return new Date(a[sortBy]) - new Date(b[sortBy]);
        return new Date(b[sortBy]) - new Date(a[sortBy]);
      });

      setDeals(data);
    } catch (err) {
      console.error("Failed to fetch deals:", err);
      setToast({ open: true, message: "Failed to fetch deals", severity: "error" });
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch products for dropdown
  const fetchProducts = async () => {
    if (!shopSlug) return;
    try {
      const res = await api.get(`/shops/${shopSlug}/products`);
      setProducts(res.data || []);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setProducts([]);
    }
  };

  useEffect(() => {
    if (isPremium) {
      fetchDeals();
      fetchProducts();
    }
  }, [shopSlug, isPremium, sortBy, order]);

  const handleOpenForm = (deal = null) => {
    setSelectedDeal(deal);

    if (deal) {
      // Convert start/end time for datetime-local input
      const formatDateTime = (dt) =>
        dt ? dt.replace(" ", "T").slice(0, 16) : "";
      setForm({
        title: deal.title,
        description: deal.description || "",
        discount: deal.discount || 0,
        product_id: deal.product_id || "",
        start_time: formatDateTime(deal.start_time),
        end_time: formatDateTime(deal.end_time),
        highlight_type: deal.highlight_type || "lunch",
        is_featured: deal.is_featured || false,
      });
    } else {
      setForm({
        title: "",
        description: "",
        discount: "",
        product_id: "",
        start_time: "",
        end_time: "",
        highlight_type: "lunch",
        is_featured: false,
      });
    }
    setFormOpen(true);
  };

  const handleSave = async () => {
    try {
      if (selectedDeal) {
        await api.put(`/deals/${selectedDeal.id}`, form);
        setToast({ open: true, message: "Deal updated", severity: "success" });
      } else {
        await api.post(`/shops/${shopSlug}/deals`, form); // FIXED backend route
        setToast({ open: true, message: "Deal added", severity: "success" });
      }
      setFormOpen(false);
      fetchDeals();
    } catch (err) {
      console.error("Save failed:", err);
      setToast({ open: true, message: "Failed to save deal", severity: "error" });
    }
  };

  const handleDelete = async (deal) => {
    if (!window.confirm(`Delete deal "${deal.title}"?`)) return;
    try {
      await api.delete(`/deals/${deal.id}`);
      setToast({ open: true, message: "Deal deleted", severity: "success" });
      fetchDeals();
    } catch (err) {
      console.error("Delete failed:", err);
      setToast({ open: true, message: "Failed to delete deal", severity: "error" });
    }
  };

  if (!isPremium) {
    return (
      <Box mt={2}>
        <Typography color="textSecondary">
          🚫 Deals are only available for premium shops.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Actions Bar */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        mb={2}
        gap={1}
      >
        <Button
          fullWidth={isMobile}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm()}
        >
          Add Deal
        </Button>

        <FormControl size="small" sx={{ minWidth: isMobile ? "100%" : 200 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={`${sortBy}_${order}`}
            label="Sort By"
            onChange={(e) => {
              const [field, dir] = e.target.value.split("_");
              setSortBy(field);
              setOrder(dir);
            }}
          >
            {sortOptions.map((opt) => (
              <MenuItem
                key={opt.label}
                value={`${opt.value.sortBy}_${opt.value.order}`}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Deals List */}
      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={2}>
          {deals.map((deal) => (
            <Card key={deal.id} variant="outlined">
              <CardContent>
                <Typography variant="h6">{deal.title}</Typography>
                <Typography variant="body2">{deal.description}</Typography>
                <Typography variant="body2">Discount: {deal.discount}%</Typography>
                <Typography variant="body2">
                  Highlight: {deal.highlight_type}
                </Typography>
                <Typography variant="body2">
                  Featured: {deal.is_featured ? "Yes" : "No"}
                </Typography>
                {deal.product_name && (
                  <Box display="flex" alignItems="center" mt={1}>
                    {deal.product_image && (
                      <img
                        src={deal.product_image}
                        alt={deal.product_name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          marginRight: 8,
                          borderRadius: 4,
                        }}
                      />
                    )}
                    <Typography variant="body2">{deal.product_name}</Typography>
                  </Box>
                )}
                <Typography variant="body2" mt={1}>
                  {deal.start_time &&
                    new Date(deal.start_time).toLocaleString()}{" "}
                  → {deal.end_time && new Date(deal.end_time).toLocaleString()}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleOpenForm(deal)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDelete(deal)}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      ) : (
        <Box overflow="auto">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th>Title</th>
                <th>Discount</th>
                <th>Highlight</th>
                <th>Featured</th>
                <th>Product</th>
                <th>Active Dates</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((deal) => (
                <tr key={deal.id}>
                  <td>{deal.title}</td>
                  <td>{deal.discount}%</td>
                  <td>{deal.highlight_type}</td>
                  <td>{deal.is_featured ? "Yes" : "No"}</td>
                  <td>{deal.product_name}</td>
                  <td>
                    {deal.start_time &&
                      new Date(deal.start_time).toLocaleString()}{" "}
                    → {deal.end_time && new Date(deal.end_time).toLocaleString()}
                  </td>
                  <td>
                    <IconButton onClick={() => handleOpenForm(deal)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(deal)}>
                      <DeleteIcon />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedDeal ? "Edit Deal" : "Add Deal"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <TextField
              fullWidth
              label="Discount (%)"
              type="number"
              value={form.discount}
              onChange={(e) => setForm({ ...form, discount: e.target.value })}
            />
            <TextField
              select
              fullWidth
              label="Product (optional)"
              value={form.product_id || ""}
              onChange={(e) => setForm({ ...form, product_id: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {products.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  <Box display="flex" alignItems="center">
                    {p.main_image && (
                      <img
                        src={p.main_image}
                        alt={p.name}
                        style={{
                          width: 40,
                          height: 40,
                          objectFit: "cover",
                          marginRight: 8,
                          borderRadius: 4,
                        }}
                      />
                    )}
                    <Box>
                      <Typography variant="body2">{p.name}</Typography>
                      {p.price && (
                        <Typography variant="caption">Ksh {p.price}</Typography>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              type="datetime-local"
              label="Start Time"
              InputLabelProps={{ shrink: true }}
              value={form.start_time}
              onChange={(e) => setForm({ ...form, start_time: e.target.value })}
            />
            <TextField
              fullWidth
              type="datetime-local"
              label="End Time"
              InputLabelProps={{ shrink: true }}
              value={form.end_time}
              onChange={(e) => setForm({ ...form, end_time: e.target.value })}
            />
            <TextField
              select
              fullWidth
              label="Highlight Type"
              value={form.highlight_type}
              onChange={(e) =>
                setForm({ ...form, highlight_type: e.target.value })
              }
            >
              {highlightOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>Featured</Typography>
              <Switch
                checked={form.is_featured}
                onChange={(e) =>
                  setForm({ ...form, is_featured: e.target.checked })
                }
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.severity}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DealsManager;
